import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { UPLOAD_STATUS_NOT_UPLOADING } from '@polyai/common/constants/Screen.constants';

import { resetUploadStatus } from 'actions';
import type { ImportType } from 'components/organisms/ImportPanel/types';
import { UploadType } from 'components/organisms/ImportPanel/types';

export interface UploadPanelState {
  isUploadPanelOpen: {
    [key in UploadType]: boolean;
  };
  uploadsInProgress: {
    [key in UploadType]: boolean;
  };
  uploadStatus: {
    [key in UploadType]: {
      status: string;
      error: boolean;
      numberOfFiles: number;
    };
  };
  importType: {
    [key in UploadType]?: ImportType;
  };
  hideWriteModeOptions: {
    [key in UploadType]: boolean;
  };
}

const initialState: UploadPanelState = {
  isUploadPanelOpen: {
    [UploadType.TOPICS]: false,
    [UploadType.VARIANTS]: false,
    [UploadType.CUSTOM_METRICS]: false,
    [UploadType.VARIANTS_STREAM]: false,
  },
  uploadsInProgress: {
    [UploadType.TOPICS]: false,
    [UploadType.VARIANTS]: false,
    [UploadType.CUSTOM_METRICS]: false,
    [UploadType.VARIANTS_STREAM]: false,
  },
  uploadStatus: {
    [UploadType.TOPICS]: {
      status: UPLOAD_STATUS_NOT_UPLOADING,
      error: false,
      numberOfFiles: 0,
    },
    [UploadType.VARIANTS]: {
      status: UPLOAD_STATUS_NOT_UPLOADING,
      error: false,
      numberOfFiles: 0,
    },
    [UploadType.VARIANTS_STREAM]: {
      status: UPLOAD_STATUS_NOT_UPLOADING,
      error: false,
      numberOfFiles: 0,
    },
    [UploadType.CUSTOM_METRICS]: {
      status: UPLOAD_STATUS_NOT_UPLOADING,
      error: false,
      numberOfFiles: 0,
    },
  },
  importType: {
    [UploadType.TOPICS]: undefined,
    [UploadType.VARIANTS]: undefined,
    [UploadType.CUSTOM_METRICS]: undefined,
    [UploadType.VARIANTS_STREAM]: undefined,
  },
  hideWriteModeOptions: {
    [UploadType.TOPICS]: false,
    [UploadType.VARIANTS]: false,
    [UploadType.CUSTOM_METRICS]: false,
    [UploadType.VARIANTS_STREAM]: false,
  },
};

const uploadPanelSlice = createSlice({
  name: 'uploadPanel',
  initialState,
  reducers: {
    openUploadPanel: (
      state,
      data: PayloadAction<{
        uploadType: UploadType;
        importType?: ImportType;
        hideWriteModeOptions?: boolean;
      }>,
    ) => {
      state.isUploadPanelOpen[data.payload.uploadType] = true;
      state.importType[data.payload.uploadType] = data.payload.importType;
      state.hideWriteModeOptions[data.payload.uploadType] =
        data.payload.hideWriteModeOptions ?? false;
    },
    closeUploadPanel: (state, data: PayloadAction<UploadType>) => {
      state.isUploadPanelOpen[data.payload] = false;
      state.importType[data.payload] = undefined;
      state.hideWriteModeOptions[data.payload] = false;
    },
    startUploading: (state, data: PayloadAction<UploadType>) => {
      state.uploadsInProgress[data.payload] = true;
    },
    setUploadStatus: (
      state,
      data: PayloadAction<{
        uploadType: UploadType;
        data: {
          status: string;
          error: boolean;
          numberOfFiles: number;
        };
      }>,
    ) => {
      const { payload } = data;
      state.uploadStatus[payload.uploadType] = payload.data;
    },
  },

  extraReducers: builder => {
    builder.addCase(
      resetUploadStatus,
      (state, data: PayloadAction<UploadType | undefined>) => {
        const uploadType = data.payload;
        if (uploadType) {
          state.uploadStatus[uploadType] = {
            status: UPLOAD_STATUS_NOT_UPLOADING,
            error: false,
            numberOfFiles: 0,
          };
          state.uploadsInProgress[uploadType] = false;
        }
      },
    );
  },
});

export const {
  openUploadPanel,
  closeUploadPanel,
  startUploading,
  setUploadStatus,
} = uploadPanelSlice.actions;

export default uploadPanelSlice.reducer;
